import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import LocationsMapPanel from 'screens/locations/components/Locations/LocationsMapPanel'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import { getCenterTenants } from 'slices/locationSlice'

/**
 * locationSlice predates the typed-slice convention: its payload creator
 * declares no parameter type, so TypeScript infers a zero-argument action
 * creator. Widened here rather than in the slice, which other screens share.
 */
const getCenterTenantsThunk = getCenterTenants as any

/**
 * LocationMaps — the location's Map tab, built on the shared LocationsMapPanel
 * so it reads the same as the Projects and Evaluations map surfaces: a
 * vertically scrolling column of cards on the left, the MapLibre surface on the
 * right with lettered markers, the legend, and the Layers panel.
 *
 * The card list is the location itself, then the center's tenants when it is a
 * retail center. Under those sits the Other Retail container, holding nearby
 * retail that is not in our system.
 *
 * Replaces the previous Google-backed map screen and its fixed
 * 1 / 3 / 5 / all-rings action list. The Layers panel carries the same radii,
 * but each ring is now independently switchable.
 */
const LocationMaps = () => {
	const dispatch = useDispatch()
	const { id } = useParams<{ id: string }>()

	const [sheetLocation, setSheetLocation] = useState<any>(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	const { location, loading } = useSelector((state: any) => ({
		loading: state.locations.isLoading,
		location: state.locations.entities[id as string],
	}))

	// Optional chaining: this runs before the `!location` guard below, so a
	// location that has not loaded yet would otherwise throw here and blank the
	// tab rather than reaching that guard.
	const isCenter = location?.assetType === 'center'

	const { centerTenantEntities, centerTenantIds, centerTenantsInitialized } = useSelector(
		(state: any) => state.locations,
	)

	useEffect(() => {
		if (!centerTenantsInitialized && isCenter) {
			dispatch(getCenterTenantsThunk(id))
		}
	}, [dispatch, centerTenantsInitialized, id, isCenter])

	// The location itself leads the list, so it takes marker "A"; its tenants
	// follow in the order the slice returned them. Deduped in case the center
	// also comes back as one of its own tenants.
	const mapLocations = useMemo(() => {
		const tenants = (centerTenantIds || [])
			.map((tid: string) => centerTenantEntities[tid])
			.filter(Boolean)
		const out: any[] = []
		const seen = new Set<string>()
		for (const loc of [location, ...tenants]) {
			if (!loc?._id) continue
			const key = String(loc._id)
			if (seen.has(key)) continue
			seen.add(key)
			out.push(loc)
		}
		return out
	}, [location, centerTenantIds, centerTenantEntities])

	// A card or marker click opens the location's edit slider in place. Saving
	// dispatches patchLocation, which refreshes the entity this screen already
	// reads, so the card, legend and pin update without a reload.
	const handleSelectLocation = useCallback((selected: any) => {
		if (!selected?._id) return
		setSheetLocation(selected)
		setSheetOpen(true)
	}, [])

	// TODO: Create skeleton loader
	if (loading || !location) return null

	// A location with no coordinates cannot be mapped. Returning null here left
	// the whole tab blank with no explanation, which is indistinguishable from a
	// broken screen. Say what is missing and how to fix it instead.
	if (!location.latitude || !location.longitude) {
		return (
			<div className="grid grid-cols-12 gap-6">
				<div className="col-span-12">
					<div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
						<h2 className="text-[22px] font-normal text-gray-700 m-0">No map available yet</h2>
						<p className="text-sm text-gray-500 mt-2 mb-0">
							{location.assetName ? `"${location.assetName}"` : 'This location'} has no
							coordinates, so it cannot be placed on a map. Add a street address on the
							location's Edit tab and it will appear here.
						</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<>
			<LocationsMapPanel
				locations={mapLocations}
				onSelectLocation={handleSelectLocation}
				legendTitle={isCenter ? 'Center & tenants' : 'Location'}
				showOtherRetail
				anchorType="Location"
				anchorId={location?._id}
			/>

			{/* Edit only. Archive and Delete are deliberately hidden here — this is
			    the location the tab is about, and removing it from its own map tab
			    would leave the screen with nothing to show. */}
			<LocationActionSheet
				location={sheetLocation}
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				showActions={false}
			/>
		</>
	)
}

export default LocationMaps
